import React from 'react';

import UserCareManagerIcon from '@hero/ui-kit/icons/utility/UserCareManagerIcon';
import ListFilter, { ListFilterOption } from '../../../../components/ListFilter';
import FilterPillGroup, {
    FilterPillOption
} from '../../../../components/FilterPanel/FilterPillGroup';
import { useUserStatusContext } from '../../../../context/UserStatus';
import useGetCareManagers from '../../api/useGetCareManagers';
import { CareManager } from '../../types';
import * as Style from './CareManagerFilter.module.scss';

const NA_VALUE = '0';

// Below this many care managers the pills are quick to scan, so the search-within
// field is just noise.
const SEARCH_MIN_MANAGERS = 8;

const renderAvatar = (careManager?: CareManager, isActive = false) =>
    careManager?.image_url ? (
        <img src={careManager.image_url} alt="" className={Style.avatar} />
    ) : (
        <span className={Style.avatarPlaceholder}>
            {/* The icon paints its stroke from the theme, not `currentColor`, so the
                active pill's white label can't cascade into it. */}
            <UserCareManagerIcon size="small" type={isActive ? 'inverted' : 'utility'} />
        </span>
    );

const CareManagerFilter: React.FC<{ inline?: boolean }> = ({ inline = false }) => {
    const { data: careManagers } = useGetCareManagers();
    const { userStatus, permissions } = useUserStatusContext();
    const [search, setSearch] = React.useState('');

    // When the logged-in user is themselves in the care-manager roster, surface their
    // own entry first. Roster membership is the exact test — `userStatus.role` reports
    // the PRIMARY org's role, which is wrong once the user switches to another org.
    const currentCareManager = React.useMemo(
        () => careManagers?.find((cm) => cm.hero_user_id === userStatus?.hero_user_id),
        [careManagers, userStatus]
    );

    const orderedManagers = React.useMemo(() => {
        if (!careManagers) return [];
        if (!currentCareManager) return careManagers;
        return [
            currentCareManager,
            ...careManagers.filter((cm) => cm.id !== currentCareManager.id)
        ];
    }, [careManagers, currentCareManager]);

    // Full option set (drives selection validity, the count badge and chips).
    const options: ListFilterOption[] = React.useMemo(
        () => [
            ...orderedManagers.map((cm) => ({
                value: String(cm.id),
                label: `${cm.first_name} ${cm.last_name}`
            })),
            { value: NA_VALUE, label: 'N/A' }
        ],
        [orderedManagers]
    );

    const formatLabel = React.useCallback(
        (selectedValues: string[]) => {
            if (selectedValues.length === 1) {
                if (selectedValues[0] === NA_VALUE) return 'N/A';
                const cm = careManagers?.find((c) => String(c.id) === selectedValues[0]);
                if (cm) return `${cm.first_name} ${cm.last_name}`;
            }
            if (selectedValues.length > 1) {
                return `${selectedValues.length} Care Managers`;
            }
            return 'Care Manager';
        },
        [careManagers]
    );

    const renderContent = React.useCallback(
        (_options: ListFilterOption[], selectedValues: string[], onToggle: (v: string) => void) => {
            // Guard the term on `showSearch` too, so a list that shrinks below the
            // threshold can't stay silently filtered by a now-hidden input.
            const showSearch = orderedManagers.length >= SEARCH_MIN_MANAGERS;
            const term = showSearch ? search.trim().toLowerCase() : '';
            const matched = orderedManagers.filter((cm) =>
                `${cm.first_name} ${cm.last_name}`.toLowerCase().includes(term)
            );

            const pillOptions: FilterPillOption[] = [
                ...matched.map((cm) => ({
                    value: String(cm.id),
                    label: `${cm.first_name} ${cm.last_name}${
                        cm.id === currentCareManager?.id ? ' (You)' : ''
                    }`,
                    leading: renderAvatar(cm, selectedValues.includes(String(cm.id)))
                })),
                ...(!term || 'n/a'.includes(term)
                    ? [
                          {
                              value: NA_VALUE,
                              label: 'N/A',
                              leading: renderAvatar(undefined, selectedValues.includes(NA_VALUE))
                          }
                      ]
                    : [])
            ];

            return (
                <>
                    {showSearch ? (
                        <input
                            className={Style.search}
                            placeholder="Search care managers"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    ) : null}
                    {pillOptions.length ? (
                        <FilterPillGroup
                            options={pillOptions}
                            selectedValues={selectedValues}
                            onToggle={onToggle}
                        />
                    ) : (
                        <p className={Style.empty}>No care managers found</p>
                    )}
                </>
            );
        },
        [orderedManagers, currentCareManager, search]
    );

    // HWM-23020: only roles with the Care Manager module VIEW bit (Care Manager,
    // Supervisor, Admin) may see/use the CM filter. Standard / Read Only never fetch
    // the CM list, so the filter is hidden for them entirely.
    if (!permissions.careManager.careManagerView) {
        return null;
    }

    return (
        <ListFilter
            options={options}
            searchParam="care_manager_ids"
            label="Care Manager"
            mode="multi"
            hasResetBtn
            width="28rem"
            resetPaginationKeys={['limit', 'offset']}
            formatLabel={formatLabel}
            renderContent={renderContent}
            disabled={!careManagers?.length}
            inline={inline}
        />
    );
};

export default CareManagerFilter;
